import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, Wrench, Sparkles } from "lucide-react";
import { api } from "../lib/api";

export default function RingResize() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    order_number: "",
    product_description: "",
    current_size: "",
    desired_size: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast("Please provide your name and email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/resize-requests", form);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not submit. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div data-testid="ring-resize-page" className="pt-8 max-w-[1400px] mx-auto px-6 sm:px-12 pb-24 min-h-screen">
      <Link
        to="/"
        className="label-track text-[#737373] hover:text-[#0A0A0A] inline-flex items-center gap-1 mb-8"
      >
        <ArrowLeft size={14} /> Home
      </Link>

      <div className="grid lg:grid-cols-2 gap-16 items-start">
        {/* Left: hero copy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <p className="label-track text-[#C8A165] mb-4">Complimentary Service</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] leading-[1.05]">
            Ring Resize
          </h1>
          <p className="mt-6 label-track text-[#737373]">
            Complimentary within 60 days of delivery
          </p>
          <p className="mt-8 text-[#525252] leading-relaxed max-w-lg">
            Fit matters. Every Dreamonds ring includes one complimentary resize within the first 60 days
            of delivery. Whether your ring finger has changed a half size or a full, our atelier will
            adjust the band by hand and return it insured, at no cost to you.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5 max-w-lg">
            {[
              { Icon: Clock, title: "Free within 60 days", body: "One complimentary resize on any Dreamonds ring within 60 days of delivery." },
              { Icon: Wrench, title: "Hand adjusted", body: "Precision resizing in our Canadian atelier by the master jeweler who made your piece." },
              { Icon: Sparkles, title: "Repolished & insured", body: "Returned repolished, rhodium refreshed where applicable, and fully insured both ways." },
              { Icon: CheckCircle2, title: "Lifetime after that", body: "Beyond 60 days we still resize your piece at a flat atelier fee. Ask us for a quote." },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-3">
                <f.Icon size={18} strokeWidth={1.4} className="text-[#C8A165] mt-0.5 shrink-0" />
                <div>
                  <p className="font-heading text-lg text-[#0A0A0A] leading-tight">{f.title}</p>
                  <p className="text-sm text-[#737373] mt-1 leading-relaxed">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right: form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="bg-[#FAFAF7] p-8 sm:p-10 border border-[#EFE7D8]"
        >
          {sent ? (
            <div data-testid="resize-success" className="py-12 text-center">
              <CheckCircle2 size={44} strokeWidth={1.2} className="text-[#C8A165] mx-auto mb-6" />
              <h3 className="font-heading text-3xl text-[#0A0A0A]">Request received.</h3>
              <p className="text-[#525252] mt-4 max-w-sm mx-auto leading-relaxed">
                Our atelier team will reach out within 1 business day with the next steps and a
                prepaid insured shipping label.
              </p>
              <Link
                to="/"
                className="mt-8 inline-block label-track text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 hover:text-[#C8A165] hover:border-[#C8A165] transition-colors"
              >
                Return home
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="resize-form" className="grid grid-cols-2 gap-x-5 gap-y-5">
              <p className="col-span-2 label-track text-[#C8A165]">Resize Request</p>
              <h2 className="col-span-2 font-heading text-2xl text-[#0A0A0A] -mt-2 mb-2">
                Tell us about your ring.
              </h2>

              <div>
                <label className="label-track text-[#737373] text-[10px]">Full Name *</label>
                <input
                  data-testid="resize-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
                />
              </div>
              <div>
                <label className="label-track text-[#737373] text-[10px]">Email *</label>
                <input
                  data-testid="resize-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
                />
              </div>
              <div>
                <label className="label-track text-[#737373] text-[10px]">Phone (optional)</label>
                <input
                  data-testid="resize-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
                />
              </div>
              <div>
                <label className="label-track text-[#737373] text-[10px]">Order Number</label>
                <input
                  data-testid="resize-order"
                  value={form.order_number}
                  onChange={(e) => setForm({ ...form, order_number: e.target.value })}
                  placeholder="DR-XXXX"
                  className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
                />
              </div>
              <div className="col-span-2">
                <label className="label-track text-[#737373] text-[10px]">Ring Description</label>
                <input
                  data-testid="resize-product"
                  value={form.product_description}
                  onChange={(e) => setForm({ ...form, product_description: e.target.value })}
                  placeholder="Halcyon, 1ct Round, 18K Yellow"
                  className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
                />
              </div>
              <div>
                <label className="label-track text-[#737373] text-[10px]">Current Size (US)</label>
                <input
                  data-testid="resize-current"
                  value={form.current_size}
                  onChange={(e) => setForm({ ...form, current_size: e.target.value })}
                  placeholder="7"
                  className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
                />
              </div>
              <div>
                <label className="label-track text-[#737373] text-[10px]">Desired Size (US)</label>
                <input
                  data-testid="resize-desired"
                  value={form.desired_size}
                  onChange={(e) => setForm({ ...form, desired_size: e.target.value })}
                  placeholder="7.5"
                  className="w-full bg-transparent border-b border-[#D4D4D4] focus:border-[#0A0A0A] outline-none py-2 mt-1"
                />
              </div>
              <div className="col-span-2">
                <label className="label-track text-[#737373] text-[10px]">Additional Notes</label>
                <textarea
                  data-testid="resize-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={4}
                  className="w-full bg-white border border-[#D4D4D4] focus:border-[#0A0A0A] outline-none p-3 mt-2 text-sm"
                  placeholder="Anything else we should know before the atelier picks up your piece."
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="resize-submit"
                className="col-span-2 mt-3 bg-[#0A0A0A] text-white py-4 label-track hover:bg-[#C8A165] transition-colors disabled:opacity-50"
              >
                {busy ? "Sending…" : "Request Free Resize"}
              </button>
              <p className="col-span-2 text-xs text-[#737373]">
                We reply within 1 business day with a prepaid insured shipping label.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
